import { Link, useFetcher, useNavigate } from '@remix-run/react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ChevronDown,
  ChevronRight,
  Bell,
  Loader2,
  LogIn,
  LogOut,
  Menu,
  Search,
  ShoppingBag,
  ShoppingCart,
  User,
  UserPlus,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRootLoader } from '~/utils/use-root-loader';
import { Price } from '~/components/products/Price';
import { getIconForCollection } from '~/utils/category-icons';
import { API_URL } from '~/constants';
import { clearAllSessionData } from '~/utils/auth-helper';
import { MerchantSwitcher } from './MerchantSwitcher';

export function Header({
  onCartIconClick,
  cartQuantity,
}: {
  onCartIconClick: () => void;
  cartQuantity: number;
}) {
  const data = useRootLoader();
  const isSignedIn =
    !!data.activeCustomer?.activeCustomer?.id ||
    data.merchantContext.capability.isSuperAdmin;
  const { t } = useTranslation();
  const collections = data.collections || [];
  const navigate = useNavigate();
  const fetcher = useFetcher();
  const creditFetcher = useFetcher<{
    merchantCredit: { availableCredit: number } | null;
  }>();
  const merchantCreditRequested = useRef(false);

  useEffect(() => {
    if (
      isSignedIn &&
      (!data.merchantContext.capability.canSwitchMerchant ||
        data.merchantContext.activeMerchant) &&
      !merchantCreditRequested.current
    ) {
      merchantCreditRequested.current = true;
      creditFetcher.load('/api/checkout-merchant?creditOnly=1');
    }
  }, [
    creditFetcher,
    data.merchantContext.activeMerchant,
    data.merchantContext.capability.canSwitchMerchant,
    isSignedIn,
  ]);

  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const logoutFetcher = useFetcher();

  // Close profile dropdown and search suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileRef.current &&
        !profileRef.current.contains(event.target as Node)
      ) {
        setProfileOpen(false);
      }
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // this helps to ensure that profile dropdown is closed when user log in
  useEffect(() => {
    setProfileOpen(false);
  }, [isSignedIn]);

  // Handle fetcher completion
  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data) {
      setIsSearching(false);
    }
  }, [fetcher.state, fetcher.data]);

  // Handle logout completion - force full page reload
  useEffect(() => {
    if (logoutFetcher.state === 'idle' && logoutFetcher.data) {
      // Force full page reload to clear all cached state
      window.location.href = '/';
    }
  }, [logoutFetcher.state, logoutFetcher.data]);

  // Debounce search
  useEffect(() => {
    if (searchTerm.trim().length > 2) {
      setIsSearching(true); // User is typing/waiting, so we are "searching"
      const timer = setTimeout(() => {
        fetcher.load(
          `/api/suggestions?q=${encodeURIComponent(searchTerm.trim())}`,
        );
        setShowSuggestions(true);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setShowSuggestions(false);
      setIsSearching(false);
    }
  }, [searchTerm]);

  // Helper to get initials
  const getInitials = () => {
    if (!data.activeCustomer?.activeCustomer) return 'JD';
    const { firstName, lastName } = data.activeCustomer.activeCustomer;
    return (
      `${firstName?.charAt(0) || ''}${
        lastName?.charAt(0) || ''
      }`.toUpperCase() || 'JD'
    );
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      setShowSuggestions(false);
      window.location.href = `/search?q=${encodeURIComponent(
        searchTerm.trim(),
      )}`;
    }
  };

  // Filter to only show top-level collections (same as Sidebar)
  const topLevelCollections = collections.filter(
    (c) => !c.parent || c.parent.name === '__root_collection__',
  );

  const suggestions = (fetcher.data as any)?.items || [];

  const handleSsoLogin = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      // Clear session data before new login attempt
      clearAllSessionData();

      const ssoEndpoint =
        API_URL.replace('/shop-api', '/sso/login-url') + '?_t=' + Date.now();
      const response = await fetch(ssoEndpoint);
      const data = (await response.json()) as { url?: string };
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('SSO Redirect Error:', error);
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-100">
      {/* ---------------- Main Navbar ---------------- */}
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16 gap-4 lg:gap-6">
          {/* ----------- Logo ----------- */}
          <div className="flex items-center flex-shrink-0 lg:w-[180px] xl:w-[200px]">
            <Link to="/" className="flex-shrink-0">
              <img
                src="/logo.svg"
                alt={t('common.logoAlt') || 'Redtab'}
                className="h-7 sm:h-8 w-auto"
              />
            </Link>
          </div>

          {/* ----------- Search Bar (Desktop) - Aligned with main content ----------- */}
          <div className="hidden md:flex flex-1" ref={searchRef}>
            <form
              onSubmit={handleSearch}
              className="relative flex items-center w-full"
            >
              <div className="flex items-center w-full justify-between transition-all overflow-hidden h-12 rounded-full pl-6 pr-2 py-2 bg-white border border-[#E5E5E5] focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => {
                    if (searchTerm.trim().length > 2) setShowSuggestions(true);
                  }}
                  placeholder="Search products..."
                  className="flex-1 bg-transparent text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0 border-none shadow-none"
                />
                {/* Search Button - Red circle with white icon */}
                <button
                  type="submit"
                  className="flex items-center justify-center text-white hover:opacity-90 transition-colors flex-shrink-0 w-8 h-8 rounded-full p-1.5 bg-[#EC0045]"
                >
                  <Search className="w-4 h-4" />
                </button>
              </div>

              {/* Suggestions Dropdown */}
              <AnimatePresence>
                {showSuggestions && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-14 left-0 w-full bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-[100] py-2"
                  >
                    {isSearching || fetcher.state === 'loading' ? (
                      <div className="px-4 py-8 text-center text-gray-400 text-sm flex flex-col items-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin text-primary" />
                        <span>Suggesting...</span>
                      </div>
                    ) : (
                      <>
                        {suggestions.length > 0 ? (
                          <div className="max-h-[360px] overflow-y-auto">
                            {suggestions.map((item: any) => {
                              const priceWithTax = item.priceWithTax;
                              const currentPrice =
                                typeof priceWithTax === 'object' &&
                                'value' in priceWithTax
                                  ? priceWithTax.value
                                  : typeof priceWithTax === 'object' &&
                                    'min' in priceWithTax
                                  ? priceWithTax.min
                                  : 0;
                              const originalPrice =
                                item.productVariant?.customFields
                                  ?.originalPrice;
                              const hasDiscount =
                                (originalPrice ?? 0) > currentPrice;

                              return (
                                <Link
                                  key={item.productId}
                                  to={`/products/${item.slug}`}
                                  onClick={() => setShowSuggestions(false)}
                                  className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50 transition-colors group"
                                >
                                  <div className="h-12 w-12 flex-shrink-0 rounded-lg bg-gray-100 p-1 flex items-center justify-center">
                                    {item.productAsset?.preview ? (
                                      <img
                                        src={`${item.productAsset.preview}?w=100&h=100&format=webp`}
                                        alt={item.productName}
                                        className="h-full w-full object-contain"
                                      />
                                    ) : (
                                      <div className="text-[10px] text-gray-400">
                                        No Img
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-medium text-gray-900 group-hover:text-primary truncate">
                                      {item.productName}
                                      {item.categoryName && (
                                        <>
                                          <span className="text-gray-300 mx-1">
                                            -
                                          </span>
                                          <span className="text-gray-400 font-normal text-xs uppercase">
                                            {item.categoryName}
                                          </span>
                                        </>
                                      )}
                                    </h4>
                                    <div className="text-xs font-bold text-gray-500 mt-0.5 flex items-center gap-2">
                                      <Price
                                        priceWithTax={item.priceWithTax}
                                        currencyCode={item.currencyCode}
                                      />
                                      {hasDiscount && (
                                        <div className="relative inline-block">
                                          <span className="text-gray-400 text-[10px]">
                                            <Price
                                              priceWithTax={originalPrice}
                                              currencyCode={item.currencyCode}
                                            />
                                          </span>
                                          <div className="absolute top-1/2 left-0 w-full h-[1px] bg-[#E80245] -translate-y-1/2 transform rotate-[-3deg]"></div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </Link>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="px-4 py-8 text-center text-gray-500 text-sm">
                            <p>No products found matching "{searchTerm}"</p>
                          </div>
                        )}

                        <div className="px-4 pt-2 pb-1 border-t border-gray-100 mt-1">
                          <button
                            onClick={() => {
                              setShowSuggestions(false);
                              window.location.href = `/search?q=${encodeURIComponent(
                                searchTerm.trim(),
                              )}`;
                            }}
                            className="w-full text-center text-xs font-semibold text-primary hover:text-primary/80 py-2"
                          >
                            View all results for "{searchTerm}"
                          </button>
                        </div>
                      </>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </form>
          </div>

          {/* ----------- Right Side: Signup, Login, Cart ----------- */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Mobile hamburger menu button */}
            <button
              className="md:hidden p-1.5 sm:p-2 rounded-md border border-gray-200 text-gray-600 hover:text-primary hover:border-primary transition-colors"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>

            {/* Cart Icon - Visible to all users */}
            <button
              className="relative p-2.5 bg-gray-100 text-black rounded-full hover:bg-gray-200 transition-colors"
              onClick={onCartIconClick}
              aria-label="Open cart"
            >
              <ShoppingBag className="w-5 h-5" />
              {cartQuantity ? (
                <div className="absolute -top-1.5 -right-1.5 bg-[#EC0045] min-w-[20px] h-5 rounded-full flex items-center justify-center text-[11px] font-bold text-white px-1 border-2 border-white">
                  {cartQuantity}
                </div>
              ) : null}
            </button>

            {/* Desktop: Signup & Login Buttons */}
            {!isSignedIn ? (
              <div className="hidden md:flex items-center gap-2">
                {/* Login - Red/primary background */}
                <Link
                  to="#"
                  onClick={handleSsoLogin}
                  className="text-white rounded-full hover:opacity-90 transition-all duration-200 flex items-center justify-center w-[86px] h-11 px-6 py-2 bg-[#E80245] font-semibold text-sm tracking-tight"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  Login
                </Link>
              </div>
            ) : (
              <div className="flex items-center gap-2 sm:gap-4">
                {data.merchantContext.capability.canSwitchMerchant && (
                  <MerchantSwitcher state={data.merchantContext} />
                )}
                {/* Notifications - Logged in style */}
                <button
                  className="relative p-2.5 bg-gray-100 text-black rounded-full hover:bg-gray-200 transition-colors hidden sm:block"
                  aria-label="Notifications"
                >
                  <Bell className="w-5 h-5" />
                </button>

                {/* Credit Limit Section */}
                {creditFetcher.data?.merchantCredit && (
                  <div className="hidden lg:flex flex-col items-end mr-2 text-right">
                    <span className="text-[#00A859] font-bold text-sm leading-tight">
                      {creditFetcher.data.merchantCredit.availableCredit.toLocaleString(
                        'en-US',
                      )}{' '}
                      रु
                    </span>
                    <span className="text-gray-500 text-[10px] uppercase font-medium leading-tight">
                      Credit Limit
                    </span>
                  </div>
                )}

                {/* Profile Avatar with Dropdown */}
                <div className="relative hidden md:block" ref={profileRef}>
                  <button
                    onClick={() => setProfileOpen((prev) => !prev)}
                    className="relative flex items-center justify-center w-10 h-10 rounded-full bg-pink-100 transition-all hover:opacity-90"
                    title={t('account.myAccount')}
                  >
                    <span className="text-[#EC0045] font-bold text-sm">
                      {getInitials()}
                    </span>

                    {/* Tiny Red Chevron Circle overlay */}
                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-[#EC0045] rounded-full flex items-center justify-center border border-white">
                      <ChevronDown className="w-3 h-3 text-white" />
                    </div>
                  </button>

                  {/* Dropdown menu */}
                  <AnimatePresence>
                    {profileOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ duration: 0.1 }}
                        className="absolute right-0 mt-3 w-56 bg-white border border-gray-100 rounded-xl shadow-xl z-50 py-2 overflow-hidden ring-1 ring-black ring-opacity-5"
                      >
                        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {data.activeCustomer?.activeCustomer?.firstName ||
                              'User'}{' '}
                            {data.activeCustomer?.activeCustomer?.lastName ||
                              ''}
                          </p>
                          <p className="text-xs text-gray-500 truncate mt-0.5">
                            {data.activeCustomer?.activeCustomer?.emailAddress}
                          </p>
                        </div>

                        <a
                          href="https://dev-merchant.redtab.xyz/dashboard"
                          className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          onClick={() => setProfileOpen(false)}
                        >
                          <User className="w-4 h-4 text-gray-400" />
                          My Dashboard
                        </a>

                        <Link
                          to="/account/history"
                          className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          onClick={() => setProfileOpen(false)}
                        >
                          <ShoppingBag className="w-4 h-4 text-gray-400" />
                          My Past Orders
                        </Link>

                        <div className="border-t border-gray-100 my-1" />

                        <logoutFetcher.Form
                          method="post"
                          action="/api/logout"
                          onSubmit={() => clearAllSessionData()}
                        >
                          <button
                            type="submit"
                            className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 font-medium transition-colors"
                          >
                            <LogOut className="w-4 h-4" />
                            {t('account.signOut')}
                          </button>
                        </logoutFetcher.Form>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ---------------- Mobile Drawer ---------------- */}
      <div
        className={`fixed inset-0 z-50 md:hidden transition-opacity duration-500 ease-in-out ${
          menuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        {/* Overlay */}
        <div
          className="absolute inset-0 bg-black/40"
          onClick={() => setMenuOpen(false)}
        />

        {/* Drawer menu */}
        <motion.div
          initial={{ x: '-100%' }}
          animate={{ x: menuOpen ? 0 : '-100%' }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="absolute top-0 left-0 h-full w-[85vw] sm:w-80 bg-white shadow-lg p-4 sm:p-6 flex flex-col overflow-y-auto"
        >
          {/* Drawer Header */}
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <img src="/logo.svg" alt="Redtab" className="h-6 sm:h-8" />
            <button
              onClick={() => setMenuOpen(false)}
              className="p-1.5 sm:p-2 rounded-md hover:bg-gray-100 text-gray-500 hover:text-red-500 transition-colors"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>

          {/* Mobile Search */}
          <div className="mb-4 relative">
            <form onSubmit={handleSearch}>
              <div className="flex items-center w-full justify-between transition-all overflow-hidden h-12 rounded-full pl-6 pr-2 py-2 bg-white border border-[#E5E5E5] focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => {
                    if (searchTerm.trim().length > 2) setShowSuggestions(true);
                  }}
                  placeholder="Search products..."
                  className="flex-1 bg-transparent text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0 border-none shadow-none"
                />
                <button
                  type="submit"
                  className="flex items-center justify-center text-white hover:opacity-90 transition-colors flex-shrink-0 w-8 h-8 rounded-full p-1.5 bg-[#EC0045]"
                >
                  <Search className="w-4 h-4" />
                </button>
              </div>
            </form>
            {/* Mobile Suggestions Dropdown */}
            <AnimatePresence>
              {showSuggestions && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-12 left-0 w-full bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-[100] py-2 max-h-[60vh] overflow-y-auto"
                >
                  {isSearching || fetcher.state === 'loading' ? (
                    <div className="px-4 py-8 text-center text-gray-400 text-sm flex flex-col items-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin text-primary" />
                      <span>Suggesting...</span>
                    </div>
                  ) : (
                    <>
                      {suggestions.length > 0 ? (
                        <div className="">
                          {suggestions.map((item: any) => {
                            const priceWithTax = item.priceWithTax;
                            const currentPrice =
                              typeof priceWithTax === 'object' &&
                              'value' in priceWithTax
                                ? priceWithTax.value
                                : typeof priceWithTax === 'object' &&
                                  'min' in priceWithTax
                                ? priceWithTax.min
                                : 0;
                            const originalPrice =
                              item.productVariant?.customFields?.originalPrice;
                            const hasDiscount =
                              originalPrice && originalPrice > currentPrice;

                            return (
                              <Link
                                key={item.productId}
                                to={`/products/${item.slug}`}
                                onClick={() => {
                                  setShowSuggestions(false);
                                  setMenuOpen(false);
                                }}
                                className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 transition-colors group border-b border-gray-50 last:border-0"
                              >
                                <div className="h-10 w-10 flex-shrink-0 rounded-lg bg-gray-100 p-1 flex items-center justify-center">
                                  {item.productAsset?.preview ? (
                                    <img
                                      src={`${item.productAsset.preview}?w=100&h=100&format=webp`}
                                      alt={item.productName}
                                      className="h-full w-full object-contain"
                                    />
                                  ) : (
                                    <div className="text-[8px] text-gray-400">
                                      No Img
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="text-xs font-medium text-gray-900 group-hover:text-primary truncate">
                                    {item.productName}
                                  </h4>
                                  <div className="text-[10px] font-bold text-gray-500 mt-0.5 flex items-center gap-2">
                                    <Price
                                      priceWithTax={item.priceWithTax}
                                      currencyCode={item.currencyCode}
                                    />
                                    {hasDiscount && (
                                      <div className="relative inline-block">
                                        <span className="text-gray-400 text-[8px]">
                                          <Price
                                            priceWithTax={originalPrice}
                                            currencyCode={item.currencyCode}
                                          />
                                        </span>
                                        <div className="absolute top-1/2 left-0 w-full h-[1px] bg-[#E80245] -translate-y-1/2 transform rotate-[-3deg]"></div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </Link>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="px-4 py-8 text-center text-gray-500 text-sm">
                          <p>No products found matching "{searchTerm}"</p>
                        </div>
                      )}
                      <div className="px-3 pt-2 pb-1 bg-gray-50 mt-1 sticky bottom-0">
                        <button
                          onClick={() => {
                            setShowSuggestions(false);
                            setMenuOpen(false);
                            window.location.href = `/search?q=${encodeURIComponent(
                              searchTerm.trim(),
                            )}`;
                          }}
                          className="w-full text-center text-xs font-semibold text-primary hover:text-primary/80 py-2"
                        >
                          View all results
                        </button>
                      </div>
                    </>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {isSignedIn && data.merchantContext.capability.canSwitchMerchant && (
            <MerchantSwitcher state={data.merchantContext} mobile />
          )}

          {/* Mobile Links */}
          <nav className="flex flex-col gap-1.5 sm:gap-2 text-gray-700 font-medium text-sm sm:text-base">
            {/* Login Button Only - Mobile */}
            {!isSignedIn && (
              <Link
                to="#"
                onClick={(e) => {
                  setMenuOpen(false);
                  handleSsoLogin(e);
                }}
                className="flex items-center gap-3 px-4 py-3 bg-[#E80245] text-white rounded-full hover:bg-[#E80245]/90 transition-all duration-200 font-semibold mb-3 justify-center"
              >
                <LogIn className="w-5 h-5" />
                Login
              </Link>
            )}

            {/* Categories */}
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-3 py-2 mt-2">
              Categories
            </div>
            {topLevelCollections.slice(0, 50).map((collection) => {
              const iconClass = getIconForCollection(collection.name);
              return (
                <Link
                  key={collection.id}
                  to={`/collections/${collection.slug}`}
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-gray-50 text-[#54454A] transition-all duration-200 group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-gray-400 group-hover:text-primary transition-colors">
                      <i className={`${iconClass} text-xl`} />
                    </div>
                    <span className="group-hover:text-primary transition-colors font-medium text-sm">
                      {collection.name}
                    </span>
                  </div>
                  <ChevronRight
                    size={16}
                    className="text-gray-300 group-hover:text-primary/50 transition-colors"
                  />
                </Link>
              );
            })}

            {isSignedIn && (
              <>
                <div className="border-t border-gray-100 my-2" />
                <a
                  href="https://dev-merchant.redtab.xyz/dashboard"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-primary/5 hover:text-primary transition-all duration-200"
                >
                  <User className="w-5 h-5" />
                  My Dashboard
                </a>

                <Link
                  to="/account/history"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-primary/5 hover:text-primary transition-all duration-200"
                >
                  <ShoppingBag className="w-5 h-5" />
                  My Past Orders
                </Link>

                <logoutFetcher.Form
                  method="post"
                  action="/api/logout"
                  onSubmit={() => {
                    setMenuOpen(false);
                    clearAllSessionData();
                  }}
                >
                  <button
                    type="submit"
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-red-600 hover:bg-red-50 font-medium transition-all duration-200"
                  >
                    <LogOut className="w-5 h-5" />
                    {t('account.signOut')}
                  </button>
                </logoutFetcher.Form>
              </>
            )}
          </nav>

          <hr className="my-4 sm:my-6" />

          {/* WhatsApp Contact Button */}
          <a
            href="https://wa.me/9779863335289"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-auto inline-flex items-center justify-center gap-2.5 rounded-xl px-6 w-full bg-[#25D366] hover:bg-[#20bd5a] text-white text-sm sm:text-base h-11 sm:h-12 font-bold shadow-lg hover:shadow-xl transition-all active:scale-95"
          >
            <svg
              className="w-5 h-5 sm:w-6 sm:h-6"
              fill="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
            </svg>
            <span>Contact</span>
          </a>
        </motion.div>
      </div>
    </header>
  );
}
